import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export interface CalendarEvent {
  id: number;
  title: string;
  description: string | null;
  project_id: number | null;
  owner_user_id: number;
  starts_at: number;
  ends_at: number;
  all_day: boolean;
  location: string | null;
  color: string | null;
  recurrence_rule: string | null;
  attendees: string[];
  created_at: number;
  updated_at: number;
  owner_name?: string;
  project_case_number?: string | null;
  project_title?: string | null;
}

export interface CalendarEventsResponse {
  events: CalendarEvent[];
}

export interface CalendarFilters {
  from?: number;
  to?: number;
  project_id?: number;
  owner_user_id?: number | 'me';
}

export function useCalendarEvents(filters: CalendarFilters = {}) {
  const params = new URLSearchParams();
  if (filters.from !== undefined) params.set('from', String(filters.from));
  if (filters.to !== undefined) params.set('to', String(filters.to));
  if (filters.project_id !== undefined) params.set('project_id', String(filters.project_id));
  if (filters.owner_user_id !== undefined) {
    params.set('owner_user_id', String(filters.owner_user_id));
  }
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/calendar-events?${qs}` : '/api/calendar-events';
  return useQuery({
    queryKey: ['calendar-events', { ...filters }],
    queryFn: ({ signal }) => api.get<CalendarEventsResponse>(url, signal),
  });
}

export interface CreateCalendarEventInput {
  title: string;
  description?: string;
  project_id?: number;
  starts_at: number;
  ends_at: number;
  all_day?: boolean;
  location?: string;
  color?: string;
  attendees?: string[];
}

export function useCreateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCalendarEventInput) =>
      api.post<{ event: CalendarEvent }>('/api/calendar-events', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });
}

export function useDeleteCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/calendar-events/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar-events'] });
    },
  });
}
